import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { API_BASE_URL } from '../../config';
import axiosInstance from '../../services/axiosInstance';
import { KnowledgeHubEntity } from './types';

function absoluteBackendUrl(path?: string): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export function getKnowledgeHubEntityAssetId(entity: KnowledgeHubEntity): string {
  return String(entity.source?.assetId || (entity.source?.collection === 'assets' ? entity.source?.id : '') || '');
}

export function openKnowledgeHubEntityAsset(entity: KnowledgeHubEntity): void {
  const assetId = getKnowledgeHubEntityAssetId(entity);
  if (!assetId) return;

  window.dispatchEvent(new CustomEvent('dam:open-asset-details', {
    detail: { assetId, preserveCurrentView: true },
  }));
}

export async function downloadKnowledgeHubEntityAsset(entity: KnowledgeHubEntity): Promise<void> {
  const assetId = getKnowledgeHubEntityAssetId(entity);
  if (!assetId) return;

  const fileName = entity.metadata?.fileName || entity.title || 'asset';
  const response = await axiosInstance.get(`/api/assets/${assetId}/download`, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

interface KnowledgeHubEntityCardProps {
  entity: KnowledgeHubEntity;
  compact?: boolean;
  referenceNumber?: number;
  referenceLabel?: string;
  score?: number;
  showScore?: boolean;
  onUseAsContext?: (entity: KnowledgeHubEntity) => void;
}

const KnowledgeHubEntityCard: React.FC<KnowledgeHubEntityCardProps> = ({
  entity,
  compact = false,
  referenceNumber,
  referenceLabel = 'Källa',
  score,
  showScore = false,
  onUseAsContext,
}) => {
  const metadata = entity.metadata || {};
  const categories = metadata.categoryNames || [];
  const previewPath = metadata.previewPaths?.thumbnail || metadata.previewPaths?.medium;
  const previewUrl = absoluteBackendUrl(previewPath);
  const assetId = getKnowledgeHubEntityAssetId(entity);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, minWidth: 0, overflow: 'hidden' }}>
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        <Stack direction={{ xs: 'column', sm: compact ? 'column' : 'row' }} spacing={1.5}>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt={entity.title}
              loading="lazy"
              sx={{
                width: compact ? 96 : { xs: '100%', sm: 148 },
                height: compact ? 72 : 112,
                objectFit: 'contain',
                bgcolor: '#f7f8fa',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
              }}
            />
          )}
          <Stack spacing={0.8} sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              {referenceNumber && <Chip size="small" color="primary" label={`${referenceLabel} [${referenceNumber}]`} />}
              <Chip size="small" variant={referenceNumber ? 'outlined' : 'filled'} color="primary" label={entity.entityType} />
              {showScore && typeof score === 'number' && <Chip size="small" variant="outlined" label={`Score ${score.toFixed(2)}`} />}
              {entity.articleNumbers?.slice(0, 3).map((articleNumber) => (
                <Chip key={articleNumber} size="small" variant="outlined" label={articleNumber} />
              ))}
            </Stack>
            <Typography variant={compact ? 'body2' : 'subtitle1'} sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
              {entity.title}
            </Typography>
            {(metadata.templateName || categories.length > 0) && (
              <Typography variant="caption" color="text.secondary">
                {[metadata.templateName, ...categories].filter(Boolean).join(' | ')}
              </Typography>
            )}
            {!compact && entity.tags && entity.tags.length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                {entity.tags.slice(0, 8).map((tag) => (
                  <Chip key={tag} size="small" variant="outlined" label={tag} />
                ))}
              </Stack>
            )}
            {(assetId || onUseAsContext) && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {assetId && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => openKnowledgeHubEntityAsset(entity)}>
                      Öppna asset
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => {
                        void downloadKnowledgeHubEntityAsset(entity);
                      }}
                    >
                      Ladda ner
                    </Button>
                  </>
                )}
                {onUseAsContext && (
                  <Button size="small" variant="text" onClick={() => onUseAsContext(entity)}>
                    Fokusera på denna
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default KnowledgeHubEntityCard;
